import { queryClient, useToastedMutation } from '../../../../ReactQuery';
import { post } from '../../../../redux/api';
import type {
  ImportRepositoriesRequest,
  ImportRepositoriesResponse,
} from '../../../../../shared/types/routes/vcs/githubApp';
import type { VcsApp } from '../../../../../shared/vcs/githubApp/apps';

/**
 * What a single import call carries: everything in the request except `connectionId`,
 * which is fixed per hook instance (the account the repos were listed under).
 */
export type ImportGitHubAppRepositoriesVariables = Omit<ImportRepositoriesRequest, 'connectionId'>;

const importGitHubAppRepositories = async (
  app: VcsApp,
  connectionId: string,
  variables: ImportGitHubAppRepositoriesVariables,
) => {
  const response = await post<ImportRepositoriesRequest, ImportRepositoriesResponse>(
    `/api/vcs/github-apps/${app}/import`,
    { connectionId, ...variables },
  );
  return response.data as ImportRepositoriesResponse;
};

/**
 * Imports the selected repositories, scoped to `connectionId` (the account the repos were listed under),
 * then invalidates the repository listing so each repo's connected/connectable status refreshes.
 */
export function useImportGitHubAppRepositories(app: VcsApp, connectionId: string) {
  return useToastedMutation({
    mutationFn: (variables: ImportGitHubAppRepositoriesVariables) =>
      importGitHubAppRepositories(app, connectionId, variables),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vcs', 'github-apps', app, 'repositories'] });
    },
    successToastMessage: 'Repositories imported',
    errorToastMessage: 'Failed to import repositories',
  });
}
